import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  BarChart3, ArrowUpRight, ArrowDownRight, DollarSign, Building2,
  UserCircle, ChevronLeft, ChevronRight, RefreshCw, TrendingUp
} from "lucide-react";

const MESES_NOME = [
  "Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];
const ANOS = Array.from({ length: 25 }, (_, i) => 2026 + i);

const formatCurrency = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

export default function HubGeralPage() {
  const { authAxios } = useAuth();
  const [ano, setAno] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/hub-geral/consolidado?ano=${ano}`);
      setData(res.data);
    } catch {
      toast.error("Erro ao carregar consolidado");
    } finally {
      setLoading(false);
    }
  }, [authAxios, ano]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totais = data?.totais || {};

  return (
    <div className="space-y-6" data-testid="hub-geral-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-emerald-500/10">
            <BarChart3 className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Hub Geral</h2>
            <p className="text-sm text-muted-foreground">Consolidado financeiro anual</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setAno(ano - 1)} data-testid="prev-year-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
            <SelectTrigger className="w-[100px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ANOS.map((a) => (
                <SelectItem key={a} value={String(a)}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => setAno(ano + 1)} data-testid="next-year-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* Totais Anuais */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Card className="border-emerald-500/20 bg-emerald-500/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowUpRight className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs text-muted-foreground">Total Entradas</span>
                </div>
                <p className="text-base font-bold text-emerald-400" data-testid="total-entradas">{formatCurrency(totais.total_entradas || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-red-500/20 bg-red-500/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowDownRight className="w-4 h-4 text-red-400" />
                  <span className="text-xs text-muted-foreground">Total Saidas</span>
                </div>
                <p className="text-base font-bold text-red-400" data-testid="total-saidas">{formatCurrency(totais.total_saidas || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-blue-500/20 bg-blue-500/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-blue-400" />
                  <span className="text-xs text-muted-foreground">Saldo Geral</span>
                </div>
                <p className={`text-base font-bold ${(totais.saldo || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="total-saldo">{formatCurrency(totais.saldo || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-blue-500/20 bg-blue-500/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 text-blue-400" />
                  <span className="text-xs text-muted-foreground">Empresa Entradas</span>
                </div>
                <p className="text-base font-bold text-blue-400">{formatCurrency(totais.empresa_entradas || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-orange-500/20 bg-orange-500/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 text-orange-400" />
                  <span className="text-xs text-muted-foreground">Empresa Saidas</span>
                </div>
                <p className="text-base font-bold text-orange-400">{formatCurrency(totais.empresa_saidas || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-purple-500/20 bg-purple-500/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <UserCircle className="w-4 h-4 text-purple-400" />
                  <span className="text-xs text-muted-foreground">Pessoal Saidas</span>
                </div>
                <p className="text-base font-bold text-purple-400">{formatCurrency(totais.pessoal_saidas || 0)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabela Mensal Consolidada */}
          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Detalhamento Mensal - {ano}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mes</TableHead>
                      <TableHead className="text-right text-emerald-400">Clientes Pagos</TableHead>
                      <TableHead className="text-right text-emerald-400">Revendas</TableHead>
                      <TableHead className="text-right text-red-400">Investimentos</TableHead>
                      <TableHead className="text-right text-red-400">Parceiros</TableHead>
                      <TableHead className="text-right text-orange-400">Desp. Variaveis</TableHead>
                      <TableHead className="text-right text-purple-400">Desp. Fixas</TableHead>
                      <TableHead className="text-right text-emerald-400">Entradas</TableHead>
                      <TableHead className="text-right text-red-400">Saidas</TableHead>
                      <TableHead className="text-right font-bold">Saldo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.meses.map((m) => (
                      <TableRow key={m.mes}>
                        <TableCell className="font-medium">{m.nome_mes}</TableCell>
                        <TableCell className="text-right text-emerald-400">{formatCurrency(m.detalhe.clientes_pagos)}</TableCell>
                        <TableCell className="text-right text-emerald-400">{formatCurrency(m.detalhe.revendas)}</TableCell>
                        <TableCell className="text-right text-red-400">{formatCurrency(m.detalhe.investimentos)}</TableCell>
                        <TableCell className="text-right text-red-400">{formatCurrency(m.detalhe.parceiros)}</TableCell>
                        <TableCell className="text-right text-orange-400">{formatCurrency(m.detalhe.despesas_variaveis)}</TableCell>
                        <TableCell className="text-right text-purple-400">{formatCurrency(m.detalhe.despesas_fixas)}</TableCell>
                        <TableCell className="text-right text-emerald-400 font-semibold">{formatCurrency(m.total_entradas)}</TableCell>
                        <TableCell className="text-right text-red-400 font-semibold">{formatCurrency(m.total_saidas)}</TableCell>
                        <TableCell className={`text-right font-bold ${m.saldo >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatCurrency(m.saldo)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow className="border-t-2 border-border bg-muted/30">
                      <TableCell className="font-bold">TOTAL</TableCell>
                      <TableCell className="text-right text-emerald-400 font-bold">
                        {formatCurrency(data.meses.reduce((a, m) => a + m.detalhe.clientes_pagos, 0))}
                      </TableCell>
                      <TableCell className="text-right text-emerald-400 font-bold">
                        {formatCurrency(data.meses.reduce((a, m) => a + m.detalhe.revendas, 0))}
                      </TableCell>
                      <TableCell className="text-right text-red-400 font-bold">
                        {formatCurrency(data.meses.reduce((a, m) => a + m.detalhe.investimentos, 0))}
                      </TableCell>
                      <TableCell className="text-right text-red-400 font-bold">
                        {formatCurrency(data.meses.reduce((a, m) => a + m.detalhe.parceiros, 0))}
                      </TableCell>
                      <TableCell className="text-right text-orange-400 font-bold">
                        {formatCurrency(data.meses.reduce((a, m) => a + m.detalhe.despesas_variaveis, 0))}
                      </TableCell>
                      <TableCell className="text-right text-purple-400 font-bold">
                        {formatCurrency(data.meses.reduce((a, m) => a + m.detalhe.despesas_fixas, 0))}
                      </TableCell>
                      <TableCell className="text-right text-emerald-400 font-bold">
                        {formatCurrency(totais.total_entradas || 0)}
                      </TableCell>
                      <TableCell className="text-right text-red-400 font-bold">
                        {formatCurrency(totais.total_saidas || 0)}
                      </TableCell>
                      <TableCell className={`text-right font-bold text-lg ${(totais.saldo || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrency(totais.saldo || 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
